// src/cannedjobs.js
import React, { useState, useEffect } from 'react';
import TitleBar from './components/ui/TitleBar';
import SavedToastOverlay from './components/SavedToastOverlay';

import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
const API_BASE = getApiBase();

const compareVisibleNames = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, {
  sensitivity: 'base',
  numeric: true
});

const CannedJobs = () => {
  const [jobs, setJobs] = useState([]);

  // ✅ Removed isTaxable from canned jobs
  const [formData, setFormData] = useState({
    id: null,
    jobName: '',
    price: '',
    priceType: 'Fixed'
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // ✅ Load from DB/API (source of truth)
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/canned-jobs`, {
          headers: { ...getTenantHeaders() }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Failed to load canned jobs');

        const mapped = Array.isArray(data)
          ? data.map((row) => ({
              id: row.id,
              jobName: row.name || '',
              // table has DECIMAL, might come as string/number/null
              price: row.price === null || typeof row.price === 'undefined' ? '' : String(row.price),
              // We store priceType inside `description` since DB schema doesn't have priceType column
              priceType: (row.description || 'Fixed').trim() || 'Fixed'
            }))
          : [];

        if (!cancelled) setJobs(mapped);
      } catch (e) {
        console.error(e);
        if (!cancelled) setJobs([]);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Clean legacy fields (if any)
    const cleaned = { ...formData };
    delete cleaned.isTaxable;

    const payload = {
      name: String(cleaned.jobName || '').trim(),
      description: String(cleaned.priceType || 'Fixed').trim(),
      price:
        cleaned.price === '' || cleaned.price === null || typeof cleaned.price === 'undefined'
          ? null
          : Number(cleaned.price),
      taxable: 0
    };

    if (!payload.name) return;

    try {
      if (cleaned.id) {
        // UPDATE
        const res = await fetch(`${API_BASE}/api/canned-jobs/${cleaned.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Failed to update job');

        setJobs(
          (jobs || []).map((j) =>
            String(j.id) === String(cleaned.id)
              ? {
                  ...j,
                  jobName: payload.name,
                  priceType: payload.description || 'Fixed',
                  price: payload.price === null ? '' : String(payload.price)
                }
              : j
          )
        );

        setConfirmation('');
        window.dispatchEvent(new CustomEvent('jobflow:saved', {
          detail: { message: 'JOB UPDATED!' }
        }));
      } else {
        // CREATE
        const res = await fetch(`${API_BASE}/api/canned-jobs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Failed to create job');

        const newId = data?.id;
        if (!Number.isInteger(newId) || newId <= 0) {
          throw new Error('Failed to create job: invalid authoritative ID');
        }

        setJobs([
          {
            id: newId,
            jobName: payload.name,
            priceType: payload.description || 'Fixed',
            price: payload.price === null ? '' : String(payload.price)
          },
          ...(jobs || [])
        ]);

        setConfirmation('');
        window.dispatchEvent(new CustomEvent('jobflow:saved', {
          detail: { message: 'JOB SAVED!' }
        }));
      }

      setTimeout(() => setConfirmation(''), 3000);

      // ✅ Reset without isTaxable
      setFormData({ id: null, jobName: '', price: '', priceType: 'Fixed' });
    } catch (err) {
      console.error(err);
      setConfirmation('ERROR SAVING JOB ❌');
      setTimeout(() => setConfirmation(''), 3500);
    }
  };

  const deleteJob = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;

    try {
      const res = await fetch(`${API_BASE}/api/canned-jobs/${id}`, {
        method: 'DELETE',
        headers: { ...getTenantHeaders() }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to delete job');

      setJobs((jobs || []).filter((item) => String(item.id) !== String(id)));
    } catch (err) {
      console.error(err);
      setConfirmation('ERROR DELETING JOB ❌');
      setTimeout(() => setConfirmation(''), 3500);
    }
  };

  const filteredJobs = (jobs || [])
    .filter((j) => (j.jobName || '').toLowerCase().includes((searchTerm || '').toLowerCase()))
    .sort((a, b) => compareVisibleNames(a.jobName, b.jobName));

  const styles = {
    container: { padding: isMobile ? '15px' : '40px', maxWidth: '1400px' },

    form: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
      gap: '20px',
      backgroundColor: '#E7B888',
      padding: isMobile ? '15px' : '30px',
      border: isMobile ? '6px solid black' : '8px solid black',
      marginBottom: '40px'
    },

    input: {
      padding: '15px',
      border: '4px solid black',
      fontWeight: '900',
      fontSize: '16px',
      backgroundColor: 'white',
      width: '100%',
      boxSizing: 'border-box'
    },

    searchWrap: {
      width: '100%',
      padding: '6px',
      marginBottom: '20px',
      backgroundColor: '#E7B888',
      boxSizing: 'border-box'
    },

    searchBar: {
      width: '100%',
      padding: '15px',
      border: '8px solid black',
      fontSize: '18px',
      fontWeight: '900',
      backgroundColor: 'white',
      boxSizing: 'border-box'
    },

    label: { fontWeight: '900', fontSize: '13px', marginBottom: '8px', display: 'block', textTransform: 'uppercase' },

    button: {
      gridColumn: isMobile ? 'auto' : 'span 4',
      padding: '20px',
      backgroundColor: 'black',
      color: '#FFA500',
      fontWeight: '900',
      border: '4px solid #E8761D',
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '20px'
    },

    jobList: {
      display: 'grid',
      gap: '14px',
      width: '100%',
      border: '10px solid black',
      padding: '12px',
      backgroundColor: '#E7B888',
      boxSizing: 'border-box'
    },
    jobCard: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      justifyContent: 'space-between',
      gap: isMobile ? '14px' : '24px',
      padding: isMobile ? '16px' : '18px 20px',
      border: '8px solid black',
      backgroundColor: 'white',
      boxSizing: 'border-box',
      width: '100%',
      minWidth: 0
    },
    jobInfo: { flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere' },
    jobName: { fontSize: '18px', fontWeight: 900, marginBottom: '8px' },
    jobDetails: { display: 'flex', flexWrap: 'wrap', gap: '6px 22px', fontSize: '14px', fontWeight: 800 },
    jobRate: { color: '#054d3b', fontWeight: 900 },
    actionGroup: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      justifyContent: isMobile ? 'flex-start' : 'flex-end',
      flex: '0 0 auto',
      maxWidth: '100%'
    },

    // ✅ table action buttons (now real buttons, consistent hover)
    actionBtn: {
      background: '#ffffff',
      color: 'black',
      border: '4px solid black',
      padding: '10px 12px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      marginRight: '10px'
    },

    actionBtnDanger: {
      background: '#ff4444',
      color: 'white',
      border: '4px solid black',
      padding: '10px 12px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase'
    },

    // ✅ top title-bar right button style
    topBtn: {
      background: 'black',
      color: '#FFA500',
      border: '5px solid #E8761D',
      padding: '14px 16px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase'
    }
  };

  return (
    <div className="jfn-canned-jobs-page" style={styles.container}>
      <SavedToastOverlay accentColor="#E7B888" />
      {/* Hover Effects for ALL action buttons in Canned Jobs */}
      <style>{`
        @media (max-width: 767px) {
          .jfn-canned-jobs-page {
            padding-left: 6px !important;
            padding-right: 6px !important;
          }
        }
        .jfn-cj-btn{
          transition: transform 120ms ease, filter 120ms ease, border-color 120ms ease, background 120ms ease, color 120ms ease;
        }
        .jfn-cj-btn:hover{
          transform: translateY(-1px);
          filter: brightness(1.03);
          background: yellow !important;
          color: black !important;
          border-color: black !important;
        }
      `}</style>

      {/* MAIN TITLE BAR */}
      <TitleBar
        icon="🛠️"
        title="CANNED JOBS / LABOR CATALOG"
        accent="#E7B888"
        right={
          <button
            className="jfn-cj-btn"
            style={styles.topBtn}
            type="button"
            onClick={() => (window.location.href = '/dashboard')}
          >
            ← BACK TO DASHBOARD
          </button>
        }
      />

      <form style={styles.form} onSubmit={handleSubmit}>
        {confirmation && (
          <div
            style={{
              gridColumn: isMobile ? 'auto' : 'span 4',
              color: '#065f46',
              fontWeight: '900',
              textAlign: 'center',
              marginBottom: '10px'
            }}
          >
            {confirmation}
          </div>
        )}

        <div style={{ gridColumn: isMobile ? 'auto' : 'span 2' }}>
          <label style={styles.label}>Service Name / Description</label>
          <input
            style={styles.input}
            name="jobName"
            value={formData.jobName}
            onChange={handleInputChange}
            placeholder="E.G. SQUARE FOOTAGE SANITIZING"
            required
          />
        </div>

        <div>
          <label style={styles.label}>Price Type</label>
          <select style={styles.input} name="priceType" value={formData.priceType} onChange={handleInputChange}>
            <option value="Fixed">Fixed Rate</option>
            <option value="Hourly">Hourly Rate</option>
            <option value="Variable">Variable / Unit</option>
          </select>
        </div>

        {/* ✅ Spacer to keep the 4-column grid structure the same (tax removed, layout stays stable) */}
        <div />

        <div style={{ gridColumn: isMobile ? 'auto' : 'span 4' }}>
          <label style={styles.label}>
            Default Rate ($) - <small>Leave at 0 if variable</small>
          </label>
          <input
            style={styles.input}
            type="number"
            name="price"
            value={formData.price}
            onChange={handleInputChange}
            step="0.01"
          />
        </div>

        <button className="jfn-cj-btn" style={styles.button} type="submit">
          {formData.id ? 'Update Catalog Item' : 'Secure Job to Catalog'}
        </button>
      </form>

      {/* SEARCH SECTION TITLE BAR */}
      <TitleBar icon="🔎" title="SEARCH LABOR CATALOG" accent="#E7B888" />

      <div style={styles.searchWrap}>
        <input
          style={styles.searchBar}
          placeholder="SEARCH JOBS..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div style={styles.jobList}>
        {filteredJobs.map((j) => {
          const n = Number(j.price);
          const hasPrice = Number.isFinite(n) && n > 0;

          return (
            <article key={j.id} style={styles.jobCard}>
              <div style={styles.jobInfo}>
                <div style={styles.jobName}>{j.jobName}</div>
                <div style={styles.jobDetails}>
                  <span>TYPE: {j.priceType}</span>
                  <span style={styles.jobRate}>
                    {hasPrice ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'TBD / VARIABLE'}
                    {j.priceType === 'Hourly' ? ' /hr' : j.priceType === 'Variable' ? ' /unit' : ''}
                  </span>
                </div>
              </div>

              <div style={styles.actionGroup}>
                <button
                  type="button"
                  className="jfn-cj-btn"
                  style={styles.actionBtn}
                  onClick={() => {
                    setFormData({ ...j });
                    window.scrollTo(0, 0);
                  }}
                >
                  EDIT
                </button>

                <button
                  type="button"
                  className="jfn-cj-btn"
                  style={styles.actionBtnDanger}
                  onClick={() => deleteJob(j.id)}
                >
                  DEL
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default CannedJobs;
